import React, { memo, useMemo } from 'react';
import { MEDIA_TYPE } from '@whatsper/texterchat-common';
import classNames from 'classnames';
import { Document, Page, pdfjs } from 'react-pdf';
import Icon, { FileFilled } from '@ant-design/icons';
import { Trans } from 'react-i18next';
import LoadingIcon from '../common/LoadingIcon';
import eyeSvg from '../../../assets/icons/noGroupIcons/eye.svg';
import { readableFileSize, truncateFilename } from '../../../helpers';
import { getFileUrl } from '../../../store/FilesStore';
import VoiceNotePlayer from '../../Base/VoiceNotePlayer';
pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();
import s from './FilePreview.module.scss';
const Preview = ({ file, fileId, filename, type, pageHeight, onPDFLoadSuccess, compact, renderFileIcon }) => {
    var _a;
    if (!file && !fileId)
        return null;
    const displayName = (file === null || file === void 0 ? void 0 : file.name) || filename || 'file';
    const name = truncateFilename(displayName, 28);
    const size = file ? readableFileSize(file.size) : '';
    const ext = ((_a = displayName.split('.').pop()) === null || _a === void 0 ? void 0 : _a.toLowerCase()) || '';
    const contentType = file === null || file === void 0 ? void 0 : file.type;
    const previewUrl = useMemo(() => {
        if (file) {
            return URL.createObjectURL(file);
        }
        if (fileId) {
            return getFileUrl(fileId);
        }
        return '';
    }, [file, fileId]);
    const pdfFile = useMemo(() => file || { url: previewUrl, withCredentials: true }, [file, previewUrl]);
    const notAvailablePreview = (React.createElement("div", { className: classNames(s.controlsBoxPreview, s.mainPreview) },
        React.createElement("div", { className: s.previewBox },
            React.createElement("div", { className: s.previewIcon }, renderFileIcon(displayName, contentType)),
            React.createElement("div", { className: s.previewText },
                React.createElement("div", { className: s.previewTitle }, name),
                React.createElement("div", { className: s.previewSize }, size)))));
    switch (type) {
        case MEDIA_TYPE.IMAGE:
            return React.createElement("img", { src: previewUrl, alt: name, className: s.innerContent });
        case MEDIA_TYPE.VIDEO:
            return (React.createElement("video", { className: classNames(s.innerContent, {
                    [s.compactVideo]: compact,
                }), controls: !compact },
                React.createElement("source", { src: previewUrl })));
        case MEDIA_TYPE.AUDIO:
            if (compact) {
                return (React.createElement("div", { className: classNames(s.compactContainer, s.innerContent) },
                    React.createElement(Icon, { className: s.compactIcon, component: FileFilled }),
                    React.createElement("span", null, ext)));
            }
            return (React.createElement(VoiceNotePlayer, { src: previewUrl, seed: fileId || (file === null || file === void 0 ? void 0 : file.uid) || displayName, className: classNames(s.innerContent, s.audioPlayer) }));
        case MEDIA_TYPE.DOC:
            if (compact) {
                return (React.createElement("div", { className: classNames(s.compactContainer, s.innerContent) },
                    React.createElement(Icon, { className: s.compactIcon, component: FileFilled }),
                    React.createElement("span", null, ext)));
            }
            if (ext === 'pdf') {
                return (React.createElement("div", { className: s.innerContent },
                    React.createElement(Document, { file: pdfFile, className: s.pdfDocument, onLoadSuccess: onPDFLoadSuccess, loading: React.createElement("div", { className: s.loadingIcon },
                            React.createElement(LoadingIcon, null)) },
                        React.createElement(Page, { pageNumber: 1, height: pageHeight, className: s.pdfPage, renderTextLayer: false, renderAnnotationLayer: false }))));
            }
            else {
                return (React.createElement("div", { className: s.noPreviewContainer },
                    React.createElement("div", { className: s.noPreviewContentWrapper },
                        React.createElement("img", { src: eyeSvg, alt: "no preview available icon", className: s.previewIcon }),
                        React.createElement("div", { className: s.innerTitle },
                            React.createElement(Trans, { i18nKey: "previewNotAvailable" }))),
                    notAvailablePreview));
            }
        default:
            return (React.createElement("div", { className: s.noPreviewContainer },
                React.createElement("div", { className: s.noPreviewContentWrapper },
                    React.createElement("img", { src: eyeSvg, alt: "no preview available icon", className: s.previewIcon }),
                    React.createElement("div", { className: s.innerTitle },
                        React.createElement(Trans, { i18nKey: "previewNotAvailable" }))),
                notAvailablePreview));
    }
};
export default memo(Preview, (prevProps, nextProps) => {
    var _a, _b;
    // props change that should trigger re-render
    if (((_a = prevProps.file) === null || _a === void 0 ? void 0 : _a.uid) !== ((_b = nextProps.file) === null || _b === void 0 ? void 0 : _b.uid))
        return false;
    if (prevProps.fileId !== nextProps.fileId)
        return false;
    if (prevProps.pageHeight !== nextProps.pageHeight)
        return false;
    return true;
});
